import { BlocksSnippetAri } from '@atlassian/ari';

import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

import { reportError } from '../reportError.ts';
import { ATLASSIAN_ACCOUNT_ID, type Snippet, type SnippetOwner } from '../snippetTypes.ts';
import { parseSnippetContent } from '../snippetUtils.ts';

import type { SnippetData } from './blockServiceTypes.ts';
import { fetchUserInfo, type UserInfoQueryData } from './blockServiceUserInfoQuery.ts';

export const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const { userId, displayName } = await getSessionData();
	const currentUser = await getSnippetOwner(userId, displayName);

	return snippets
		.map((snippet: SnippetData) => transformSnippetData(snippet, displayName, currentUser))
		.filter((snippet: Snippet | undefined) => snippet !== undefined);
};

export const transformSnippet = async (snippet: SnippetData): Promise<Snippet | undefined> => {
	const { userId, displayName } = await getSessionData();
	const currentUser = await getSnippetOwner(userId, displayName);

	return transformSnippetData(snippet, displayName, currentUser);
};

export const transformSnippetData = (
	snippet: SnippetData,
	displayName: string | null,
	currentUser?: SnippetOwner,
): Snippet | undefined => {
	try {
		const parsedBody = parseSnippetContent(snippet.body);
		if (parsedBody === undefined) {
			return undefined;
		}

		const isAtlassianCreated = snippet.createdBy?.toLowerCase() === ATLASSIAN_ACCOUNT_ID;
		const createdByUser: SnippetOwner =
			currentUser ?? getDefaultUserInfo(snippet.createdBy, displayName);
		const createdBy: SnippetOwner = isAtlassianCreated
			? {
					accountId: ATLASSIAN_ACCOUNT_ID,
					displayName: 'Atlassian',
				}
			: createdByUser;

		return {
			ari: BlocksSnippetAri.parse(snippet.resourceId),
			snippetId: snippet.snippetId,
			name: snippet.name,
			description: snippet.description,
			bodyFormat: snippet.bodyFormat,
			body: parsedBody,
			product: snippet.product,
			isFavorite: Boolean(snippet.isFavorite),
			status: snippet.status,
			scope: snippet.scope,
			createdBy,
			createdAt: new Date(Number(snippet.createdAt)),
			updatedAt: new Date(Number(snippet.updatedAt)),
		};
	} catch (error) {
		reportError(
			error instanceof Error
				? error
				: new Error(`Failed to transform snippet ${snippet.snippetId}: ${String(error)}`),
		);
		return undefined;
	}
};

const getSnippetOwner = async (
	userId: string | null,
	displayName: string | null,
): Promise<SnippetOwner | undefined> => {
	if (userId) {
		const user: UserInfoQueryData | undefined = await fetchUserInfo(userId);
		if (user) {
			return {
				accountId: user.accountId,
				displayName: user.displayName ?? displayName ?? user.accountId,
				avatarUrl: user.avatarUrl ?? getDefaultUserAvatarUrl(user.accountId),
			};
		}
	}
	logNoCurrentUser();
};

const getDefaultUserInfo = (userId: string, displayName: string | null): SnippetOwner => {
	return {
		accountId: userId,
		displayName: displayName ?? userId,
		avatarUrl: getDefaultUserAvatarUrl(userId),
	};
};

const getDefaultUserAvatarUrl = (userId: string): string => {
	return `/wiki/aa-avatar/${encodeURIComponent(userId)}`;
};

const logNoCurrentUser = () => {
	const log = new Error(`No current user found when fetching snippet owner details`);
	reportError(log);
};
